"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";

// eslint-disable-next-line import/order
import type maplibregl from "maplibre-gl";

import MapGL, { Marker, useMap } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";

import { HomePinIcon } from "@/components/icons-lib";
import { MapStyleSelector } from "@/components/maps/map-style-selector";
import { publicConfig } from "@/configs/public-config";
import { ZoomControlsWrapper } from "@/providers/maps/components/zoom-controls-wrapper";
import {
  DEFAULT_CENTER,
  DEFAULT_MAP_STYLE,
  DEFAULT_LAYER_MAX_ZOOM,

  DEFAULT_ZOOM,
  MAP_LEVEL_BOUNDARIES_SOURCE_LAYER_ID,
  SCHOOL_PINS_SOURCE_LAYER_ID,
} from "@/providers/maps/constants";
import { useMapStyle } from "@/providers/maps/contexts/map-style-context";
import { clearActiveFeatureStateForRendered } from "@/providers/maps/utils/clear-active-feature-state-for-rendered";
import { setFeatureStateSafe } from "@/providers/maps/utils/set-feature-state-safe";
import type {
  IFindMySchoolSchoolItem,
  SchoolType,
} from "@/providers/store/api/find-my-school/find-my-school.interface";
import { cn } from "@/shared/helpers/cn.helper";

import { FindMySchoolLayersPanel } from "./find-my-school-layers-panel";
import {
  FindMySchoolVectorLayers,
  FMS_MAP_LEVEL_BOUNDARIES_FILL_LAYER_ID,
  FMS_MAP_LEVEL_BOUNDARIES_SOURCE_ID,
  FMS_SCHOOL_PINS_ACTIVE_LAYER_ID,
  FMS_SCHOOL_PINS_LABEL_LAYER_ID,
  FMS_SCHOOL_PINS_LAYER_ID,
  FMS_SCHOOL_PINS_SOURCE_ID,
} from "./find-my-school-vector-layers";

const FMS_MIN_ZOOM = 3;

function getMapPadding(
  mobile: boolean,
  mobileMapBottomPadding: number,
): number | maplibregl.PaddingOptions {
  if (!mobile) return 40;
  return {
    top: 76,
    bottom: Math.round(mobileMapBottomPadding + 16),
    left: 16,
    right: 16,
  };
}

export interface FindMySchoolMapProps {
  schools: IFindMySchoolSchoolItem[];
  userLocation?: { lat: number; lng: number } | null;
  highlightedSchoolId?: string | null;
  highlightedBoundaryId?: string | null;
  highlightedBoundaryBbox?: number[] | null;
  districtId?: string;
  selectedCategory?: SchoolType;
  dropPinMode?: boolean;
  onMapClick?: (lat: number, lng: number) => void;
  onDropPinComplete?: () => void;
  onSchoolClick?: (school: IFindMySchoolSchoolItem) => void;
  districtCenter?: { lat: number; lng: number } | null;
  districtBounds?: number[] | null;
  mobile?: boolean;
  mobileDrawerHeight?: number;
  mobileMapBottomPadding?: number;
  mobileControlsOpacity?: number;
}

export function FindMySchoolMap({
  schools,
  userLocation,
  highlightedSchoolId,
  highlightedBoundaryId = null,
  highlightedBoundaryBbox = null,
  districtId,
  selectedCategory = "all",
  dropPinMode = false,
  onMapClick,
  onDropPinComplete,
  onSchoolClick,
  districtCenter,
  districtBounds,
  mobile = false,
  mobileDrawerHeight = 150,
  mobileMapBottomPadding = 150,
  mobileControlsOpacity = 1,
}: FindMySchoolMapProps) {
  const mapCollection = useMap();
  const mapInstance = mapCollection.default ?? mapCollection.current;
  const { mapStyle: mapStyleByDistrict } = useMapStyle();
  const isInitialFitDoneRef = useRef(false);
  const previousUserLocationRef = useRef(userLocation);

  const mapPadding = useMemo(
    () => getMapPadding(mobile, mobileMapBottomPadding),
    [mobile, mobileMapBottomPadding],
  );
  const mapPaddingRef = useRef(mapPadding);
  const previousCameraTargetRef = useRef({
    mapPadding,
    userLocation,
    highlightedSchoolId,
  });

  useEffect(() => {
    mapPaddingRef.current = mapPadding;
  }, [mapPadding]);

  const mapStyleUrl = useMemo(() => {
    const apiKey = publicConfig.maptilerApiKey;
    if (apiKey) {
      const styleName =
        districtId != null
          ? (mapStyleByDistrict[districtId] ?? DEFAULT_MAP_STYLE)
          : DEFAULT_MAP_STYLE;
      return `https://api.maptiler.com/maps/${styleName}/style.json?key=${apiKey}`;
    }
    return "https://demotiles.maplibre.org/style.json";
  }, [districtId, mapStyleByDistrict]);

  const initialViewState = useMemo(() => {
    if (userLocation) {
      return {
        longitude: userLocation.lng,
        latitude: userLocation.lat,
        zoom: 14,
      };
    }

    if (districtCenter) {
      return {
        longitude: districtCenter.lng,
        latitude: districtCenter.lat,
        zoom: DEFAULT_ZOOM,
      };
    }

    return {
      longitude: DEFAULT_CENTER[0],
      latitude: DEFAULT_CENTER[1],
      zoom: DEFAULT_ZOOM,
    };
  }, [userLocation, districtCenter]);

  const handleMapLoad = useCallback(
    (e: maplibregl.MapLibreEvent) => {
      if (isInitialFitDoneRef.current || userLocation) return;
      if (districtBounds?.length === 4) {
        isInitialFitDoneRef.current = true;
        e.target.fitBounds(
          [[districtBounds[0], districtBounds[1]], [districtBounds[2], districtBounds[3]]],
          { padding: mapPadding, duration: 0 },
        );
      }
    },
    [userLocation, districtBounds, mapPadding],
  );

  useEffect(() => {
    const previousUserLocation = previousUserLocationRef.current;
    previousUserLocationRef.current = userLocation;
    if (!mapInstance) return;

    if (userLocation) {
      mapInstance.flyTo({
        center: [userLocation.lng, userLocation.lat],
        zoom: 14,
        duration: 700,
        padding: mapPaddingRef.current,
      });
      return;
    }

    if (previousUserLocation && districtBounds?.length === 4) {
      mapInstance.fitBounds(
        [[districtBounds[0], districtBounds[1]], [districtBounds[2], districtBounds[3]]],
        { padding: mapPaddingRef.current, duration: 500 },
      );
    }
  }, [districtBounds, mapInstance, userLocation]);

  useEffect(() => {
    if (highlightedSchoolId && mapInstance) {
      const school = schools.find((s) => s.id === highlightedSchoolId);
      if (school?.latitude && school?.longitude) {
        mapInstance.flyTo({
          center: [school.longitude, school.latitude],
          duration: 500,
          padding: mapPaddingRef.current,
        });
      }
    }
  }, [highlightedSchoolId, schools, mapInstance]);

  useEffect(() => {
    if (!highlightedBoundaryId || !mapInstance) return;
    if (highlightedBoundaryBbox?.length !== 4) return;
    mapInstance.fitBounds(
      [
        [highlightedBoundaryBbox[0], highlightedBoundaryBbox[1]],
        [highlightedBoundaryBbox[2], highlightedBoundaryBbox[3]],
      ],
      { padding: mapPaddingRef.current, duration: 500, maxZoom: 15 },
    );
  }, [highlightedBoundaryId, highlightedBoundaryBbox, mapInstance]);

  useEffect(() => {
    const previousTarget = previousCameraTargetRef.current;
    previousCameraTargetRef.current = {
      mapPadding,
      userLocation,
      highlightedSchoolId,
    };
    if (!mapInstance || previousTarget.mapPadding === mapPadding) return;
    if (
      previousTarget.userLocation !== userLocation ||
      previousTarget.highlightedSchoolId !== highlightedSchoolId
    ) {
      return;
    }

    const selectedSchool = schools.find((school) => school.id === highlightedSchoolId);
    if (selectedSchool?.latitude != null && selectedSchool.longitude != null) {
      mapInstance.easeTo({
        center: [selectedSchool.longitude, selectedSchool.latitude],
        padding: mapPadding,
        duration: 220,
      });
      return;
    }
    if (userLocation) {
      mapInstance.easeTo({
        center: [userLocation.lng, userLocation.lat],
        padding: mapPadding,
        duration: 220,
      });
      return;
    }
    mapInstance.easeTo({ padding: mapPadding, duration: 220 });
  }, [highlightedSchoolId, mapInstance, mapPadding, schools, userLocation]);

  const handleMapClick = useCallback(
    (e: maplibregl.MapLayerMouseEvent) => {
      if (dropPinMode) {
        onMapClick?.(e.lngLat.lat, e.lngLat.lng);
        onDropPinComplete?.();
        return;
      }
      const map = e.target;
      const features = map.queryRenderedFeatures(e.point, {
        layers: [
          FMS_SCHOOL_PINS_LAYER_ID,
          FMS_SCHOOL_PINS_ACTIVE_LAYER_ID,
          FMS_SCHOOL_PINS_LABEL_LAYER_ID,
        ],
      });
      if (features.length === 0) return;
      const feature = features[0];
      const rawId = feature.id ?? feature.properties?.id;
      const schoolId = rawId != null ? String(rawId) : undefined;
      if (!schoolId) return;
      const school = schools.find((s) => s.id === schoolId || String(s.id) === schoolId);
      if (!school) return;

      clearActiveFeatureStateForRendered(map, {
        sourceId: FMS_SCHOOL_PINS_SOURCE_ID,
        sourceLayerId: SCHOOL_PINS_SOURCE_LAYER_ID,
        layerId: FMS_SCHOOL_PINS_LAYER_ID,
        setActiveFalse: (m, id) =>
          setFeatureStateSafe(m, FMS_SCHOOL_PINS_SOURCE_ID, { key: "id", value: id }, { active: false }, SCHOOL_PINS_SOURCE_LAYER_ID),
        excludeId: schoolId,
      });
      setFeatureStateSafe(
        map,
        FMS_SCHOOL_PINS_SOURCE_ID,
        { key: "id", value: schoolId },
        { active: true },
        SCHOOL_PINS_SOURCE_LAYER_ID,
      );
      onSchoolClick?.(school);
    },
    [dropPinMode, onMapClick, onDropPinComplete, schools, onSchoolClick],
  );

  const prevHighlightedIdRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapInstance?.getMap();
    if (!map) return;
    const prevId = prevHighlightedIdRef.current;
    if (prevId != null && prevId !== highlightedSchoolId) {
      setFeatureStateSafe(
        map,
        FMS_SCHOOL_PINS_SOURCE_ID,
        { key: "id", value: prevId },
        { active: false },
        SCHOOL_PINS_SOURCE_LAYER_ID,
      );
    }
    const setActiveFalse = (m: maplibregl.Map, id: string | number) =>
      setFeatureStateSafe(m, FMS_SCHOOL_PINS_SOURCE_ID, { key: "id", value: id }, { active: false }, SCHOOL_PINS_SOURCE_LAYER_ID);
    clearActiveFeatureStateForRendered(map, {
      sourceId: FMS_SCHOOL_PINS_SOURCE_ID,
      sourceLayerId: SCHOOL_PINS_SOURCE_LAYER_ID,
      layerId: FMS_SCHOOL_PINS_LAYER_ID,
      setActiveFalse,
      excludeId: highlightedSchoolId ?? undefined,
    });
    if (highlightedSchoolId) {
      setFeatureStateSafe(
        map,
        FMS_SCHOOL_PINS_SOURCE_ID,
        { key: "id", value: highlightedSchoolId },
        { active: true },
        SCHOOL_PINS_SOURCE_LAYER_ID,
      );
    }
    prevHighlightedIdRef.current = highlightedSchoolId ?? null;
  }, [highlightedSchoolId, mapInstance]);

  const prevHighlightedBoundaryIdRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapInstance?.getMap();
    if (!map) return;
    const prevId = prevHighlightedBoundaryIdRef.current;
    if (prevId != null && prevId !== highlightedBoundaryId) {
      setFeatureStateSafe(
        map,
        FMS_MAP_LEVEL_BOUNDARIES_SOURCE_ID,
        { key: "id", value: prevId },
        { active: false },
        MAP_LEVEL_BOUNDARIES_SOURCE_LAYER_ID,
      );
    }
    const setActiveFalse = (m: maplibregl.Map, id: string | number) =>
      setFeatureStateSafe(
        m,
        FMS_MAP_LEVEL_BOUNDARIES_SOURCE_ID,
        { key: "id", value: id },
        { active: false },
        MAP_LEVEL_BOUNDARIES_SOURCE_LAYER_ID,
      );
    clearActiveFeatureStateForRendered(map, {
      sourceId: FMS_MAP_LEVEL_BOUNDARIES_SOURCE_ID,
      sourceLayerId: MAP_LEVEL_BOUNDARIES_SOURCE_LAYER_ID,
      layerId: FMS_MAP_LEVEL_BOUNDARIES_FILL_LAYER_ID,
      setActiveFalse,
      excludeId: highlightedBoundaryId ?? undefined,
    });
    if (highlightedBoundaryId) {
      setFeatureStateSafe(
        map,
        FMS_MAP_LEVEL_BOUNDARIES_SOURCE_ID,
        { key: "id", value: highlightedBoundaryId },
        { active: true },
        MAP_LEVEL_BOUNDARIES_SOURCE_LAYER_ID,
      );
    }
    prevHighlightedBoundaryIdRef.current = highlightedBoundaryId ?? null;
  }, [highlightedBoundaryId, mapInstance]);

  return (
    <div className="relative h-full w-full">
      {dropPinMode && (
        <div
          className={cn(
            "pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-sm text-white shadow-md",
            mobile ? "top-[76px]" : "top-4",
          )}
        >
          {mobile ? "Tap the map to place a pin" : "Click on the map to set your location"}
        </div>
      )}
      {(!mobile || mobileControlsOpacity > 0.01) && (
        <FindMySchoolLayersPanel
          hasDistrictLayer
          controlsBottom={mobileDrawerHeight + 14}
          controlsOpacity={mobileControlsOpacity}
          mobile={mobile}
        />
      )}
      <MapGL
        initialViewState={initialViewState}
        mapStyle={mapStyleUrl}
        maxZoom={DEFAULT_LAYER_MAX_ZOOM}
        minZoom={FMS_MIN_ZOOM}
        style={{ width: "100%", height: "100%" }}
        onClick={handleMapClick}
        onLoad={handleMapLoad}
      >
        {mobile ? (
          mobileControlsOpacity > 0.01 && (
            <div
              className="absolute left-[14px] z-20"
              style={{
                bottom: mobileDrawerHeight + 14,
                opacity: mobileControlsOpacity,
                pointerEvents: mobileControlsOpacity > 0.05 ? "auto" : "none",
                transition:
                  "bottom 220ms cubic-bezier(.2,.8,.2,1), opacity 160ms linear",
              }}
            >
              <MapStyleSelector
                mobile
                districtId={districtId}
              />
            </div>
          )
        ) : (
          <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2.5">
            <ZoomControlsWrapper className="relative" />
            <MapStyleSelector districtId={districtId} />
          </div>
        )}
        {districtId && (
          <FindMySchoolVectorLayers
            districtId={districtId}
            highlightedBoundaryId={highlightedBoundaryId}
            selectedCategory={selectedCategory}
          />
        )}

        {userLocation && (
          <Marker
            anchor="center"
            latitude={userLocation.lat}
            longitude={userLocation.lng}
          >
            <HomePinIcon />
          </Marker>
        )}
      </MapGL>
    </div>
  );
}
